/**
 * 模版 PDF 导入前置校验（方案 B：物理页数限制）
 */

import { CDP_ENDPOINTS, fetchCdpEngineJson } from '@/services/cdpEngineClient';
import { getPdfPhysicalPageCount } from '@/utils/pdfThumbnail';

const DEFAULT_MAX_PDF_PHYSICAL_PAGES = 1;

/** 从环境变量读取允许的最大 PDF 物理页数，默认 1 */
export function resolveMaxPdfPhysicalPages() {
  const raw = import.meta.env.VITE_PDF_MAX_PHYSICAL_PAGES;
  const parsed = Number(String(raw ?? '').trim());
  if (Number.isFinite(parsed) && parsed > 0) return Math.floor(parsed);
  return DEFAULT_MAX_PDF_PHYSICAL_PAGES;
}

export function buildPdfPhysicalPagesError(pageCount, maxPages = resolveMaxPdfPhysicalPages()) {
  const count = Number(pageCount) || 0;
  const limit = Number(maxPages) || DEFAULT_MAX_PDF_PHYSICAL_PAGES;
  if (limit === 1) {
    return `当前仅支持单页 PDF 模版（检测到 ${count} 页），请拆分后重新导入`;
  }
  return `PDF 页数不能超过 ${limit} 页（检测到 ${count} 页），请拆分后重新导入`;
}

async function fetchPdfPhysicalPageCountFromCdp(file) {
  if (!file || typeof FormData === 'undefined') return 0;
  const formData = new FormData();
  formData.append('pdf', file, file.name || 'template.pdf');
  try {
    const result = await fetchCdpEngineJson(CDP_ENDPOINTS.pdfPageCount, {
      method: 'POST',
      body: formData,
    }, {
      timeoutMs: 30000,
      fallbackMessage: 'PDF 页数读取失败',
    });
    const count = Number(result?.pageCount ?? 0);
    return Number.isFinite(count) && count > 0 ? count : 0;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[templateImportLimits] CDP 页数读取失败', error);
    }
    return 0;
  }
}

/** 客户端 pdf.js 失败时，回退 Python CDP 服务读取物理页数 */
export async function resolvePdfPhysicalPageCount(file) {
  const clientCount = await getPdfPhysicalPageCount(file);
  if (clientCount > 0) return clientCount;
  return fetchPdfPhysicalPageCountFromCdp(file);
}

/**
 * 校验 PDF 物理页数是否符合导入限制。
 * @throws {Error} 页数超限或无法读取时
 * @returns {Promise<number>} 实际页数
 */
export async function assertPdfPhysicalPagesAllowed(
  file,
  maxPages = resolveMaxPdfPhysicalPages(),
) {
  const pageCount = await resolvePdfPhysicalPageCount(file);
  if (!pageCount) {
    throw new Error('无法读取 PDF 页数，请确认文件未损坏或稍后重试');
  }
  if (pageCount > maxPages) {
    throw new Error(buildPdfPhysicalPagesError(pageCount, maxPages));
  }
  return pageCount;
}
